import React, { useState, useCallback, useRef, useEffect } from 'react';

// ── ConfirmModal ──────────────────────────────────────────────────────────────
// Usage via hook (recommended):
//   const confirm = useConfirm();
//   await confirm({ title: 'Delete story?', message: '...', danger: true });
//
// Usage as component:
//   <ConfirmModal
//     open={open} title="..." message="..." confirmLabel="Delete"
//     danger onConfirm={fn} onCancel={fn}
//   />

const ConfirmModal = ({
  open,
  title       = 'Are you sure?',
  message     = '',
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  danger       = false,
  onConfirm,
  onCancel,
}) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onCancel?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        animation: 'fadeIn 0.15s ease',
      }}
      onClick={e => e.target === e.currentTarget && onCancel?.()}
    >
      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
      <div style={{
        background: 'var(--card-bg)',
        borderRadius: '20px',
        padding: '32px',
        maxWidth: '420px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        animation: 'slideUp 0.18s ease',
      }}>
        {/* Icon */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            background: danger ? 'var(--reject-bg)' : 'var(--purple-section)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px',
          }}>
            {danger ? '🗑️' : '⚠️'}
          </div>
        </div>

        {/* Title */}
        <h5 style={{
          textAlign: 'center', fontWeight: 700, marginBottom: '10px',
          color: 'var(--text-main)', fontSize: '18px',
        }}>
          {title}
        </h5>

        {/* Message */}
        {message && (
          <p style={{
            textAlign: 'center', color: 'var(--text-muted2)', fontSize: '14px',
            lineHeight: 1.6, marginBottom: '24px',
          }}>
            {message}
          </p>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            className="btn btn-outline-secondary rounded-pill px-4"
            style={{ fontSize: '14px', minWidth: '100px' }}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            className={`btn rounded-pill px-4 ${danger ? 'btn-danger' : 'btn-dream'}`}
            style={{ fontSize: '14px', minWidth: '100px' }}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── useConfirm hook ───────────────────────────────────────────────────────────
// Returns a function that opens the modal and resolves a Promise<boolean>.
// The ConfirmProvider must wrap your app (or at least the section that uses it).
//
// Example:
//   const confirm = useConfirm();
//   const ok = await confirm({ title: 'Delete?', danger: true });
//   if (ok) { ... }

const ConfirmContext = React.createContext(null);
const AlertContext  = React.createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({ open: false });
  const [alertState,   setAlertState]   = useState({ open: false });
  const confirmResolve = useRef(null);
  const alertResolve   = useRef(null);

  // confirm — resolves true/false
  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      confirmResolve.current = resolve;
      setConfirmState({ open: true, ...options });
    });
  }, []);

  const handleConfirm = () => {
    setConfirmState(s => ({ ...s, open: false }));
    confirmResolve.current?.(true);
  };
  const handleCancel = () => {
    setConfirmState(s => ({ ...s, open: false }));
    confirmResolve.current?.(false);
  };

  // alert — resolves when OK is clicked
  const alert = useCallback((options = {}) => {
    // Accept alert('message') shorthand or alert({ title, message, ... })
    const opts = typeof options === 'string'
      ? { title: 'Notice', message: options }
      : options;
    return new Promise((resolve) => {
      alertResolve.current = resolve;
      setAlertState({ open: true, ...opts });
    });
  }, []);

  const handleAlertClose = () => {
    setAlertState(s => ({ ...s, open: false }));
    alertResolve.current?.();
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      <AlertContext.Provider value={alert}>
        {children}

        {/* Confirm modal — two buttons */}
        <ConfirmModal
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          cancelLabel={confirmState.cancelLabel}
          danger={confirmState.danger}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />

        {/* Alert modal — OK only */}
        {alertState.open && (
          <div
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 99999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '16px',
              animation: 'fadeIn 0.15s ease',
            }}
            onClick={e => e.target === e.currentTarget && handleAlertClose()}
          >
            <div style={{
              background: 'var(--card-bg)', borderRadius: '20px', padding: '32px',
              maxWidth: '400px', width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              animation: 'slideUp 0.18s ease',
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '50%',
                  background: alertState.danger ? 'var(--reject-bg)' : alertState.success ? 'var(--green-section)' : 'var(--purple-section)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '22px',
                }}>
                  {alertState.danger ? '⚠️' : alertState.success ? '✅' : 'ℹ️'}
                </div>
              </div>
              <h5 style={{
                textAlign: 'center', fontWeight: 700, marginBottom: '10px',
                color: 'var(--text-main)', fontSize: '18px',
              }}>
                {alertState.title || 'Notice'}
              </h5>
              {alertState.message && (
                <p style={{
                  textAlign: 'center', color: 'var(--text-muted2)', fontSize: '14px',
                  lineHeight: 1.6, marginBottom: '24px',
                }}>
                  {alertState.message}
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  className="btn btn-dream rounded-pill px-5"
                  style={{ fontSize: '14px' }}
                  onClick={handleAlertClose}
                  autoFocus
                >
                  {alertState.okLabel || 'OK'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AlertContext.Provider>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
};

export const useAlert = () => {
  const ctx = React.useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used inside <ConfirmProvider>');
  return ctx;
};

export default ConfirmModal;
